import { MdList, MdRoundGridView, MdSearch } from '@teamworksdev/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTablePlaceholder } from '../../../components/table/data-table-placeholder/data-table-placeholder';
import { TabPanel, Tabs, TabTrigger } from '../../../components/tab/tab';
import { GalleryViewMode } from '../../../components/gallery/types';

interface FilteredGalleryProps<T extends { id: string }> {
  title: string;
  items: T[];
  filterSection: React.ReactNode;
  renderCard:
    | (({
        item,
        viewMode,
      }: {
        item: T;
        viewMode: GalleryViewMode;
      }) => React.ReactNode)
    | null;
  renderTableView: ({ items }: { items: T[] }) => React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
}

export const AdvancedSearchGallery = <T extends { id: string }>({
  title,
  items,
  filterSection,
  renderCard,
  renderTableView,
  isLoading,
  isError,
}: FilteredGalleryProps<T>) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<GalleryViewMode>(
    renderCard !== null ? 'grid' : 'list',
  );

  return (
    <div className="pt-6 flex flex-row gap-6 h-full w-full">
      <div className="pl-6 w-64 flex-shrink-0">{filterSection}</div>
      <div className="grow flex flex-col h-full">
        <div className="inline-flex justify-between w-full mb-6 pr-6 items-center">
          <div className="text-2xl font-semibold">{title}</div>
          {renderCard !== null && (
            <div className="flex flex-row items-center space-x-2">
              <Tabs
                size="small"
                value={viewMode}
                onChange={(_, newValue) => setViewMode(newValue)}
                className="w-[88px]"
              >
                <TabTrigger
                  value={'grid'}
                  label={<MdRoundGridView size={16} fill="black" />}
                />
                <TabTrigger value={'list'} label={<MdList size={16} />} />
              </Tabs>
            </div>
          )}
        </div>
        {isError ? (
          <div className="flex grow pr-6 w-full h-full items-center justify-center text-red-500">
            {t('somethingWentWrong')}
          </div>
        ) : isLoading ? (
          <div>{t('loading')}</div>
        ) : items.length === 0 ? (
          <div className="flex grow pr-6 w-full h-full items-center justify-center">
            <DataTablePlaceholder
              icon={<MdSearch size={24} />}
              title={t('noResultsFound')}
              description={t('noResultsFoundDescription')}
            />
          </div>
        ) : (
          <div className="flex grow pr-6 w-full h-full overflow-hidden">
            {renderCard !== null && (
              <TabPanel value={viewMode} tab="grid" className="w-full mb-6">
                <div className="h-full overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 grid-rows-[minmax(600px, auto)] gap-4 pb-1">
                    {items.map((item) => (
                      <React.Fragment key={item.id}>
                        {renderCard({ item, viewMode })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </TabPanel>
            )}
            <TabPanel value={viewMode} tab="list" className="w-full">
              <div className="h-full pb-8 w-full">
                {renderTableView({
                  items,
                })}
              </div>
            </TabPanel>
          </div>
        )}
      </div>
    </div>
  );
};
